"use client";

// A server-action form's submit that goes dim and deaf, and says what it is
// doing, while its action runs. It goes INSIDE the <form>: useFormStatus reads
// the nearest enclosing one. The form keeps its server action, so a press
// before hydration still posts.
//
// ⚠️ FOUND BY A DOUBLE-CLICK (a QA walkthrough of Reacher writes). Add a key's
// action waits on Reacher's `/whoami`, so a second click lands while the first
// is still out: the first added the key, the second came back "…7788 is already
// in the pool", and the popup stayed up saying so over a key that had just gone
// in. One submit per form, while it is pending, is the whole fix. React sets
// `pending` at the submit's own (discrete) priority, so the button is deaf
// before a double click's second click is dispatched.
//
// ⚠️ DEAF, NOT `disabled` (found by CI's user-management spec). A `disabled`
// button drops the focus it has, to <body>: the person who pressed Save in a
// popup lost their place in it, and the popup's Escape with it. So the button
// keeps focus and says it is busy with `aria-disabled`, and a press while
// pending (a click, Enter on a field of the form, which clicks the default
// button) is cancelled in onClick. It dims itself, whatever classes it was
// given.
//
// The sign-in page's three code sends use it too, so nothing looks hung: a
// send waits on Stytch for up to 15 s. So does every form on /settings/brands
// and /settings/users.
//
// ⭐ A FORM WITH SEVERAL SUBMITS (an account row on /settings/brands: Link,
// Create, Retire in one form, told apart by `name="do"`). Every one of them goes
// deaf while the form is out, and only the one that was pressed says its busy
// word: React builds the action's FormData with the submitter in it, so the
// pressed button is the one whose `name` carries its `value`. A button with no
// `name` is the form's only submit, and always the pressed one.
//
// A row form whose "Saved" note sits beside it (components/saved-note.tsx) says
// *Saving…* there; its button passes its own label as `busy`, so the same word
// is not said twice an inch apart.

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useFormStatus } from "react-dom";

/**
 * How a pending SubmitButton looks: dim, with the default cursor (lib/button.ts's
 * "opacity-50 cursor-default where it is not"). `select-none` always, and the
 * pointer left ON: a double click's second click must land on the button,
 * which cancels it, or it selects whatever word is beside it (a QA walk saw
 * "Save", then "Saving…", highlighted).
 */
const DEAF = "select-none aria-disabled:opacity-50 aria-disabled:cursor-default";

export function SubmitButton({
  className,
  testid,
  busy,
  children,
  name,
  value,
  pending: handed,
  ...rest
}: {
  className: string;
  testid?: string;
  /** What the button says while ITS press is out. */
  busy: ReactNode;
  /**
   * Busy, for a form that submits in its own onSubmit (app/settings/users/
   * add-user-form.tsx): useFormStatus never sees a press that never reached the
   * form's action, so that form says so here.
   */
  pending?: boolean;
  children: ReactNode;
  name?: string;
  value?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "disabled" | "className" | "children" | "name" | "value">) {
  const status = useFormStatus();
  const pending = status.pending || handed === true;
  const data = status.data;
  const pressed = pending && (name === undefined || data?.get(name) === value);
  return (
    <button
      {...rest}
      type="submit"
      name={name}
      value={value}
      className={`${className} ${DEAF}`}
      data-testid={testid}
      aria-disabled={pending}
      aria-busy={pressed}
      onClick={(event) => {
        if (pending) event.preventDefault();
        else rest.onClick?.(event);
      }}
    >
      {pressed ? busy : children}
    </button>
  );
}
